<template>
  <b-modal
    id="modal-select-product"
    v-model="active"
    :title="$t('เลือกสินค้าเข้าโปรโมท')"
    size="lg"
    centered
    hide-footer
  >
    <b-overlay
      :show="loading"
      rounded="sm"
    >
      <div class="p-1">
        <!-- Search & Select All controls -->
        <b-row class="mb-2 align-items-center">
          <b-col
            cols="12"
            md="6"
            class="mb-1 mb-md-0"
          >
            <b-form-input
              v-model="searchQuery"
              :placeholder="$t('Search') + '...'"
              @input="onSearch"
            />
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-md-end"
          >
            <b-form-checkbox
              v-model="selectAll"
              class="custom-control-primary mr-2 cursor-pointer"
              @change="toggleSelectAll"
            >
              <strong>{{ $t('เลือกทั้งหมดในหน้านี้') }}</strong>
            </b-form-checkbox>
            <b-badge
              variant="light-primary"
              pill
            >
              {{ $t('เลือกแล้ว') }} {{ selectedProductIds.length }} {{ $t('รายการ') }}
            </b-badge>
          </b-col>
        </b-row>

        <!-- Product Table/List -->
        <div
          class="table-responsive border rounded"
          style="max-height: 400px; overflow-y: auto;"
        >
          <b-table
            :items="productList"
            :fields="fields"
            primary-key="_id"
            show-empty
            :empty-text="$t('No matching records found')"
            hover
            striped
            small
          >
            <!-- Checkbox Cell -->
            <template #cell(checkbox)="data">
              <b-form-checkbox
                v-model="selectedProductIds"
                :value="data.item._id"
                class="custom-control-primary"
                @change="updateSelectAllState"
              />
            </template>

            <!-- Image Cell -->
            <template #cell(image)="data">
              <b-img
                v-if="data.item.images && data.item.images.length > 0"
                :src="data.item.images[0]"
                fluid
                rounded
                style="height: 45px; width: 45px; object-fit: cover;"
              />
              <b-img
                v-else
                :src="require('@/assets/images/blank/no_image.png')"
                fluid
                rounded
                style="height: 45px; width: 45px; object-fit: cover;"
              />
            </template>

            <!-- Name Cell -->
            <template #cell(name)="data">
              <div>
                <strong>{{ getProductName(data.item) }}</strong>
              </div>
            </template>
          </b-table>
        </div>

        <!-- Pagination & Actions Footer -->
        <b-row class="mt-2 align-items-center">
          <b-col
            cols="12"
            md="6"
            class="mb-1 mb-md-0"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              size="sm"
              class="mb-0"
              @input="onPageChange"
            />
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="d-flex justify-content-end"
          >
            <b-button
              variant="outline-secondary"
              class="mr-1"
              @click="closeModal"
            >
              {{ $t('Cancel') }}
            </b-button>
            <b-button
              variant="primary"
              :disabled="selectedProductIds.length === 0 || loading"
              @click="submitSelection"
            >
              <feather-icon
                icon="PlusIcon"
                class="mr-50"
              />
              {{ $t('กดเพิ่มเข้าโปรโมท') }} ({{ selectedProductIds.length }})
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
/**
 * ============================================================
 * ByteMatrix Co., Ltd.
 * https://www.bytematrix.co.th | contact@bytematrix.co.th
 * ============================================================
 * Module   : eCommerce Services - Promote
 * File     : ModalSelectProduct.vue
 * Author   : Patcharapong Ponzue <contact@ibig.dev>
 * Created  : 2026-08-02
 * Updated  : 2026-08-02
 * ============================================================
 * Description:
 *   Modal component for multi-selecting products with checkboxes,
 *   select-all page functionality, search filter, and bulk addition.
 * ============================================================
 */

import {
  BModal,
  BOverlay,
  BRow,
  BCol,
  BFormInput,
  BFormCheckbox,
  BTable,
  BButton,
  BPagination,
  BImg,
  BBadge,
} from 'bootstrap-vue'
import store from '@/store'
import storeModuleProduct from '@/store/services/eCommerce/product'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import debounce from 'debounce'

const STORE_MODULE_NAME_PRODUCT = 'product'

export default {
  components: {
    BModal,
    BOverlay,
    BRow,
    BCol,
    BFormInput,
    BFormCheckbox,
    BTable,
    BButton,
    BPagination,
    BImg,
    BBadge,
  },
  props: {
    isModalActive: {
      type: Boolean,
      required: true,
    },
    promoteId: {
      type: String,
      default: '',
    },
    storeModuleName: {
      type: String,
      default: 'promoteList',
    },
  },
  data() {
    return {
      loading: false,
      searchQuery: '',
      currentPage: 1,
      perPage: 10,
      selectedProductIds: [],
      selectAll: false,
    }
  },
  computed: {
    active: {
      get() {
        return this.isModalActive
      },
      set(val) {
        this.$emit('update:is-modal-active', val)
      },
    },
    fields() {
      return [
        { key: 'checkbox', label: '', class: 'text-center', style: 'width: 50px;' },
        { key: 'image', label: this.$t('Image'), class: 'text-center', style: 'width: 70px;' },
        { key: 'name', label: this.$t('Name'), sortable: false },
      ]
    },
    productList() {
      return store.state[STORE_MODULE_NAME_PRODUCT]?.respData != null
        ? store.state[STORE_MODULE_NAME_PRODUCT].respData.data
        : []
    },
    totalItems() {
      return store.state[STORE_MODULE_NAME_PRODUCT]?.respData != null
        ? store.state[STORE_MODULE_NAME_PRODUCT].respData.max
        : 0
    },
  },
  watch: {
    isModalActive(val) {
      if (val) {
        this.selectedProductIds = []
        this.selectAll = false
        this.currentPage = 1
        this.searchQuery = ''
        this.fetchProducts()
      }
    },
  },
  created() {
    if (!store.hasModule(STORE_MODULE_NAME_PRODUCT)) {
      store.registerModule(STORE_MODULE_NAME_PRODUCT, storeModuleProduct)
    }
    this.debouncedSearch = debounce(this.fetchProducts, 500)
  },
  methods: {
    fetchProducts() {
      this.loading = true
      const obj = {
        currentPage: this.currentPage,
        pageSize: this.perPage,
        searchQuery: this.searchQuery,
        categories: [],
      }
      store
        .dispatch(`${STORE_MODULE_NAME_PRODUCT}/get`, obj)
        .then(() => {
          this.loading = false
          this.updateSelectAllState()
        })
        .catch(error => {
          this.loading = false
          console.error('Error fetching products:', error)
        })
    },
    onSearch(value) {
      this.searchQuery = value
      this.currentPage = 1
      this.debouncedSearch()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchProducts()
    },
    toggleSelectAll(val) {
      const pageProductIds = this.productList.map(p => p._id)
      if (val) {
        pageProductIds.forEach(id => {
          if (!this.selectedProductIds.includes(id)) {
            this.selectedProductIds.push(id)
          }
        })
      } else {
        this.selectedProductIds = this.selectedProductIds.filter(id => !pageProductIds.includes(id))
      }
    },
    updateSelectAllState() {
      if (this.productList.length === 0) {
        this.selectAll = false
        return
      }
      const pageProductIds = this.productList.map(p => p._id)
      this.selectAll = pageProductIds.every(id => this.selectedProductIds.includes(id))
    },
    getProductName(item) {
      if (!item) return ''
      if (item.optionNameTranslate === 'multiple' && Array.isArray(item.nameTranslate)) {
        const langIndex = item.nameTranslate.findIndex(n => n.lang === this.$i18n.locale)
        if (langIndex > -1 && item.nameTranslate[langIndex].value) {
          return item.nameTranslate[langIndex].value
        }
        if (item.nameTranslate.length > 0 && item.nameTranslate[0].value) {
          return item.nameTranslate[0].value
        }
      }
      return item.name || ''
    },
    closeModal() {
      this.active = false
    },
    submitSelection() {
      if (this.selectedProductIds.length === 0) return

      this.loading = true
      const obj = {
        promoteId: this.promoteId,
        productIds: this.selectedProductIds,
      }

      store
        .dispatch(`${this.storeModuleName}/addMultiple`, obj)
        .then(() => {
          this.loading = false
          this.$toast({
            component: ToastificationContent,
            position: 'bottom-right',
            props: {
              title: this.$t('Success'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('เพิ่มสินค้าเข้าโปรโมทเรียบร้อยแล้ว'),
            },
          })
          this.$emit('success')
          this.closeModal()
        })
        .catch(error => {
          this.loading = false
          const errorMsg = error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'FAILED_TO_SAVE'
          this.$toast({
            component: ToastificationContent,
            position: 'bottom-right',
            props: {
              title: this.$t('Error'),
              icon: 'ErrorIcon',
              variant: 'danger',
              text: this.$t(errorMsg),
            },
          })
        })
    },
  },
}
</script>
